import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';
import useModal from 'src/components/Modals/hooks/useModal';
import usePnlModalDisplayListener from 'src/components/Modals/hooks/usePnlModalDisplayListener';
import { ModalParams, ModalType } from 'src/components/Modals/types';
import { useOnboardingFlowControl } from 'src/hooks/globalSyncs/useOnboardingFlowControl';
import { usePrivateBetaWalletWhitelistGateOnConnect } from 'src/hooks/privateBetaWalletWhitelist/usePrivateBetaWalletWhitelistGateOnConnect';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

/**
 * Manages app-wide flows for automatically showing / hiding modals
 */
export default function useAppModalEffects() {
	const { currentModal, show, hide } = useModal();

	// Controls the onboarding flow based on user status (new vs existing)
	useOnboardingFlowControl();
	usePrivateBetaWalletWhitelistGateOnConnect();

	// Shows the PnL modal when a position is closed in profit.
	usePnlModalDisplayListener();

	// Listen for `showModal` app event.
	const appEventEmitter = useAppEventEmitter();
	useEffect(() => {
		const handleShowModal = (modal: ModalParams) => {
			show(modal);
		};

		appEventEmitter.on('showModal', handleShowModal);

		return () => {
			appEventEmitter.off('showModal', handleShowModal);
		};
	}, [show, appEventEmitter]);

	// Dismiss all modals when the route changes.
	const pathname = usePathname();
	const currentModalTypeRef = useSyncedRef(currentModal?.type);
	useUpdateEffect(() => {
		const currentModalType = currentModalTypeRef.current;
		const ignoredModalTypes: ModalType[] = ['user_survey'];

		if (!!currentModalType && !ignoredModalTypes.includes(currentModalType)) {
			hide();
		}
	}, [pathname, hide]);

	// Dismiss all modals when the user disconnects. We do this in an effect
	// instead of a disconnect handler because the user can disconnect through
	// the wallet extension. Any exceptions are included in `ignoredModalTypes`.
	const isConnected = useWalletIsConnected();
	const isPreviouslyConnected = usePrevious(isConnected);

	useEffect(() => {
		const currentModalType = currentModalTypeRef.current;
		const ignoredModalTypes: ModalType[] = [
			'connect_wallet',
			'settings',
			'fee_tier',
			'dev_tools',
			'feature_flag',
			'emulation',
			'privy_dev',
			'insurance_fund_revenue',
		];

		if (
			!isConnected &&
			isPreviouslyConnected &&
			!!currentModalType &&
			!ignoredModalTypes.includes(currentModalType)
		) {
			hide();
		}
	}, [hide, isConnected]);
}
